import { fetchCached, RequestData } from 'js/helpers/service';
import debounce from 'p-debounce';

import {
  Result,
  SearchOutput,
  Source,
} from 'js/model/rainbow/search-unified/SearchOutput';
import {
  makeItemResult,
  ItemResult,
  ItemResultType,
} from 'js/components/controls/search-item-result';
import { TreatmentCategoryOutput } from 'js/model/rainbow/TreatmentCategoryOutput';
import { VenueTypeOutput } from 'js/model/rainbow/venue/VenueTypeOutput';
import { getGrowthBookHeaders } from '@treatwell/ui';

export enum ResultValueType {
  Id,
  NormalisedName,
}

/*
  Remove duplicate results.

  Duplicates can occur because of misconfigured data.
  For example two treatments with the same name, or a treatment
  alias with the same name as a treatment.

  Ideally this should not happen, but experiences shows that it does.
*/
export function dedupeResults(results: Result[]): Result[] {
  const dedupedResults: Result[] = [];

  for (const result of results) {
    if (
      dedupedResults.some(
        (existingResult) =>
          existingResult.type === result.type &&
          existingResult.data &&
          result.data &&
          existingResult.data.name === result.data.name
      )
    ) {
      console.warn('Duplicate search result', result);
    } else {
      dedupedResults.push(result);
    }
  }

  return dedupedResults;
}

async function fetchSearchResults(
  state: RequestData,
  sources: string,
  text: string,
  resultValueType: ResultValueType,
  size = 15
): Promise<ItemResult[]> {
  const growthbookMParticleHeaders = await getGrowthBookHeaders();
  const path =
    '/api/v1/search/unified?' +
    `sources=${encodeURIComponent(sources)}&` +
    `text=${encodeURIComponent(text)}&` +
    `size=${encodeURIComponent(String(size))}`;

  try {
    const data = await fetchCached<SearchOutput>(
      {
        ...state,
        requestHeaders: {
          ...state.requestHeaders,
          ...growthbookMParticleHeaders,
        },
      },
      path
    );

    if (!data || !data.results) {
      return [];
    }

    return dedupeResults(data.results)
      .map((item) => createItemResult(item, resultValueType))
      .filter((item): item is ItemResult => item !== undefined);
  } catch (e) {
    console.warn(e);

    return [];
  }
}

export function createItemResult(
  result: Result,
  resultValueType: ResultValueType
): ItemResult | undefined {
  let value;

  switch (result.type) {
    case Source.ExternalLocation:
      return makeItemResult({
        entity: {
          entityType: ItemResultType.ExternalLocation,
          entityValue: result.data.reference,
          name: result.data.name,
        },
        section: 'search',
      });

    case Source.Location:
      value =
        resultValueType === ResultValueType.Id
          ? String(result.data.id)
          : result.data.normalisedName;

      return makeItemResult({
        entity: {
          entityType: ItemResultType.Location,
          entityValue: value,
          name: result.data.name,
        },
        section: 'search',
      });

    case Source.PostalArea:
      value =
        resultValueType === ResultValueType.Id
          ? String(result.data.id)
          : result.data.normalisedName;

      return makeItemResult({
        entity: {
          entityType: ItemResultType.PostalArea,
          entityValue: value,
          name: result.data.name,
        },
        section: 'search',
      });

    case Source.PostalReference:
      value =
        resultValueType === ResultValueType.Id
          ? String(result.data.id)
          : result.data.normalisedName;

      return makeItemResult({
        entity: {
          entityType: ItemResultType.PostalReference,
          entityValue: value,
          name: result.data.name,
        },
        section: 'search',
      });

    case Source.TreatmentCategory:
      return makeTreatmentCategoryItemResultType(result.data, resultValueType);

    case Source.VenueType:
      return makeVenueTypeItemResultType(result.data, resultValueType);

    case Source.Venue:
      return makeItemResult({
        entity: {
          entityType: ItemResultType.Venue,
          entityValue: result.data.normalisedName,
          name: result.data.name,
          entityId: `${result.data.id}`,
        },
        section: 'search',
      });

    default:
      // Compile failure if cases are not exhaustive.
      // TODO create an "assertNever" function

      // No compile failure if result.type is missing from the Result discriminated union.
      // So at least log a message.
      console.warn('Unrecognised result type', result);

      return result;
  }
}

export function makeTreatmentCategoryItemResultType(
  data: TreatmentCategoryOutput,
  resultValueType: ResultValueType
): ItemResult | undefined {
  if (data.id) {
    return makeTreatmentsResultType(data, resultValueType);
  }
  return makeTreatmentTypeResultType(data, resultValueType);
}

export function makeVenueTypeItemResultType(
  data: VenueTypeOutput,
  resultValueType: ResultValueType
): ItemResult | undefined {
  if (data.id) {
    return makeVenueTypeResultType(data, resultValueType);
  }
}

function makeTreatmentsResultType(
  data: TreatmentCategoryOutput,
  resultValueType: ResultValueType
): ItemResult | undefined {
  if (data.normalisedName === undefined || data.name === undefined) {
    return undefined;
  }

  const value =
    resultValueType === ResultValueType.Id
      ? String(data.id)
      : data.normalisedName;
  return makeItemResult({
    entity: {
      entityType: ItemResultType.Treatments,
      entityValue: value,
      name: data.name,
      aliasId: data.aliasId,
      entityId: `${data.id}`,
    },
    section: 'search',
  });
}

function makeVenueTypeResultType(
  data: TreatmentCategoryOutput,
  resultValueType: ResultValueType
): ItemResult | undefined {
  if (data.normalisedName === undefined || data.name === undefined) {
    return undefined;
  }

  const value =
    resultValueType === ResultValueType.Id
      ? String(data.id)
      : data.normalisedName;
  return makeItemResult({
    entity: {
      entityType: ItemResultType.VenueType,
      entityValue: value,
      name: data.name,
      aliasId: data.aliasId,
      entityId: `${data.id}`,
    },
    section: 'search',
  });
}

function makeTreatmentTypeResultType(
  data: TreatmentCategoryOutput,
  resultValueType: ResultValueType
): ItemResult | undefined {
  if (
    data.treatmentGroup === undefined ||
    data.treatmentGroup.normalisedName === undefined ||
    data.treatmentGroup.name === undefined
  ) {
    return undefined;
  }

  const value =
    resultValueType === ResultValueType.Id
      ? String(data.treatmentGroup.id)
      : data.treatmentGroup.normalisedName;

  return makeItemResult({
    entity: {
      entityType: ItemResultType.TreatmentType,
      entityValue: value,
      name: data.treatmentGroup.name,
      aliasId: data.aliasId,
      entityId: `${data.treatmentGroupId}`,
    },
    section: 'search',
  });
}

export type SearchFunc = (
  state: RequestData,
  text: string,
  size?: number
) => Promise<ItemResult[]>;

function fetchSearchResultsDebounced(
  sources: string,
  debounceMs: number,
  resultValueType: ResultValueType = ResultValueType.Id
): SearchFunc {
  async function fetch(
    state: RequestData,
    text: string,
    size?: number
  ): Promise<ItemResult[]> {
    return await fetchSearchResults(
      state,
      sources,
      text,
      resultValueType,
      size
    );
  }

  return debounce(fetch, debounceMs);
}

export function fetchTreatmentSearchResultsDebounced(
  debounceMs: number,
  resultValueType: ResultValueType = ResultValueType.Id
): SearchFunc {
  return fetchSearchResultsDebounced(
    'treatment_category,venue_type',
    debounceMs,
    resultValueType
  );
}

export function fetchLocationSearchResultsDebounced(
  debounceMs: number,
  resultValueType: ResultValueType = ResultValueType.Id
): SearchFunc {
  return fetchSearchResultsDebounced('location', debounceMs, resultValueType);
}

export function fetchSalonNameSearchResultsDebounced(
  debounceMs: number
): SearchFunc {
  return fetchSearchResultsDebounced('venue', debounceMs);
}
